import moment from 'moment';
import { RequestTypes, TypeOfPeriod } from './Enums';
import _ from 'lodash';
import { formatAs, DateFnsFormats, getCurrentUkgUserDateTime } from '../common-client2/src/services/ukgDateTime';

export const MomentFormats = Object.freeze({
    ISO: 'YYYY-MM-DDTHH:mm:ss',
    YMD: 'YYYY-MM-DD',

    DayOfMonth: 'D',
    Month: 'M',
    Year: 'YYYY',

    MMMMYYYY: 'MMMM YYYY',
    MMMDD: 'MMM DD',
    YYMMDD: 'YYMMDD',
    MMMM: 'MMMM',
    dddd: 'dddd',

    VERBOSE_LONG_DATE_WITH_DAY: 'dddd, MMMM Do YYYY'
});

export function parseJobAndLocation(orgJob = '') {
    let lastIndex = orgJob.lastIndexOf('/');
    return {
        location: orgJob.substring(0, lastIndex),
        job: orgJob.substring(lastIndex + 1)
    }
}

export function currentHttpOptions() {
    return {
        headers: {
            Authorization: sessionStorage.getItem('authToken') ?? '',
        }
    }
}

export const getPartialShiftSliderProps = (shift, partialStartDate, partialEndDate) => {
    const startDate = moment.utc(shift.startDateTime);
    const endDate = moment.utc(shift.endDateTime);
    const startLabel = formatAs(shift.startDateTime, DateFnsFormats.TIME_ONLY);
    const endLabel = formatAs(shift.endDateTime, DateFnsFormats.TIME_ONLY);
    const maxMinutes = moment.duration(endDate.diff(startDate)).asMinutes();
    const startBalance = moment.duration(partialStartDate.diff(startDate)).asMinutes();
    const endBalance = moment.duration(endDate.diff(partialEndDate)).asMinutes();
    const endBalanceStartingAt = maxMinutes - endBalance;
    const defaultValue = [startBalance, endBalanceStartingAt];
    const step = 15;
    const numberOfMarks = Math.floor(maxMinutes / step) + 1;
    const marks = Array.from(Array(numberOfMarks)).map((x, index) => ({
        value: step * index < maxMinutes ? step * index : maxMinutes,
        label: index === 0 ? startLabel : ((index === numberOfMarks - 1) ? endLabel : null)
    }));

    return {
        defaultValue,
        marks,
        max: maxMinutes,
        step,
    }
}

const NO_ASSIGNMENT_SCOPE_KEY = "";

const getProbationEndDateForShift = (managerSetting, shift) => {
    const byAssignmentId = managerSetting.probationEndDateByAssignmentId || {};
    const perAssignment = byAssignmentId[shift.assignmentId];
    if (perAssignment !== undefined) return perAssignment;
    const fallback = byAssignmentId[NO_ASSIGNMENT_SCOPE_KEY];
    return fallback !== undefined ? fallback : null;
};

const isShiftAfterProbationEnd = (managerSetting, shift) => {
    const probationEndDate = getProbationEndDateForShift(managerSetting, shift);
    return !probationEndDate || moment(shift.startDateTime) > moment(probationEndDate);
};

/**
 * @param {*} managerSetting
 * @param {Shift.Shift[]} selectedShifts
 */
export const isProbationValidForShifts = (managerSetting, selectedShifts) =>
    !selectedShifts || !selectedShifts.length || selectedShifts.every(shift => isShiftAfterProbationEnd(managerSetting, shift));

/**
 * @param {ManagerSetting} managerSetting
 * @param {Shift.Shift[]} selectedShifts
 */
export const isDisplayShiftSwapIfMultipleRequestsAllowed = (managerSetting, selectedShifts) => managerSetting.isAllowMultipleRequests || !(
    selectedShifts?.length > 1 &&
    managerSetting.type === RequestTypes.shiftSwap &&
    managerSetting.isAllowMultipleRequests
);

/**
 * @param {ManagerSetting} setting
 * @param {Shift.Shift[]} selectedShifts
 * @param {boolean} isUseMultipleAssignment
 */
export const isAssignmentValidForGroupedShiftSwap = (setting, selectedShifts, isUseMultipleAssignment) => {
    if (!isUseMultipleAssignment) return true;
    if (setting.type !== RequestTypes.shiftSwap) return true;
    if (!setting.isGroupedShiftSwap) return true;
    if (!selectedShifts || selectedShifts.length < 2) return true;

    const firstAssignmentId = selectedShifts[0].assignmentId;
    return selectedShifts.every(s => s.assignmentId === firstAssignmentId);
};

/**
 * @param {ManagerSetting[]} managerSettings
 * @param {Shift.Shift[]} selectedShifts
 * @param {boolean} [isUseMultipleAssignment]
 */
export const getValidManagerSettings = (managerSettings, selectedShifts, isUseMultipleAssignment = false) => {
    return managerSettings.filter((setting) => (
        isProbationValidForShifts(setting, selectedShifts) &&
        isDisplayShiftSwapIfMultipleRequestsAllowed(setting, selectedShifts) &&
        isWorkRuleValidForShifts(setting, selectedShifts) &&
        isSegmentTagValidForShifts(setting, selectedShifts) &&
        isAssignmentValidForGroupedShiftSwap(setting, selectedShifts, isUseMultipleAssignment)
));
}

/**
 * 
 * @param {ManagerSetting} setting 
 * @param {string} date
 * @returns {boolean}
 */
export const isDatetimeInRequestPeriod = (date, setting) =>
    (setting.requestPeriod_TypeOfPeriod === TypeOfPeriod.Forever && setting.requestPeriodLimitInDays === null) ||
    (moment.utc(date).isBetween(moment.utc(setting.requestPeriod_StartDate), moment.utc(setting.requestPeriod_EndDate), null, '[]'));

/**
 * @param {*[]} list  TODO D. - Typing
 * @param {string} key 
 * @param {boolean} addNone 
 */
export const formatResponse = (list, key, addNone = false) => {
    if (!_.size(list)) {
        return [];
    }

    const auxList = list.map(item => { return { ...item, value: item[key], label: item.name } });
    addNone && auxList.unshift({ value: null, [key]: null, name: 'None', label: 'None' });
    return auxList;
}
/**
 * 
 * @param {number} id 
 * @param {ApiAdmin.IHyperfindQuery[]} hyperFindList 
 * @returns {string}
 */
export const getHyperfindLabel = (id, hyperFindList) => {
    const auxLabel = hyperFindList.find(hyperfind => hyperfind.value === id)
    return typeof auxLabel === 'undefined' ? id : auxLabel.label;
}

export const isWorkRuleValidForShifts = (managerSetting, selectedShifts) => {
    if (Array.isArray(selectedShifts) && selectedShifts.length === 0) {
        return true;
    }

    if (!Array.isArray(managerSetting.workRuleExcludedIds) || managerSetting.workRuleExcludedIds.length === 0) {
        return true;
    }

    const selectedWorkRuleIds = selectedShifts.flatMap(shift =>
            shift.segments.map(segment => segment.workruleRef != null && segment.workruleRef.id)
        ) 

        if (managerSetting.isPartialShiftEnabled) {
            const isAnySegmentValid = selectedWorkRuleIds.some(
                workRuleId => !managerSetting.workRuleExcludedIds.includes(workRuleId)
            );
            if (isAnySegmentValid) {
                return true;
            }
        }

    return !selectedWorkRuleIds.some(workRuleId => managerSetting.workRuleExcludedIds.includes(workRuleId));
}

export const isSegmentTagValidForShifts = (managerSetting, selectedShifts) => {
    if (!Array.isArray(selectedShifts) || selectedShifts.length === 0) {
        return true;
    }

    const selectedSegmentTagIds = selectedShifts.flatMap(shift =>
        shift.segments.flatMap(segment =>
            Array.isArray(segment.segmentTags)
                ? segment.segmentTags
                      .map(tag => tag.tagDefinitionRef?.id)
                      .filter(id => id != null)
                : []
        )
    );

    if (managerSetting.isPartialShiftEnabled) {
        const isAnySegmentValid = selectedSegmentTagIds.some(
            segmentTagId => !managerSetting.segmentTagExcludedIds.includes(segmentTagId)
        );
        if (isAnySegmentValid) {
            return true;
        }
    }

    return !selectedSegmentTagIds.some(segmentTagId =>
        managerSetting.segmentTagExcludedIds.includes(segmentTagId)
    );
};

const isSettingVisibleAndEnabled = (setting) => {
    return setting.isVisible && setting.isAllowedTimeAfterShiftRuleEnabled && setting.afterShiftRuleLength != null;
}

const isValidAfterShiftRuleLength = (setting) => {
    const afterShiftRuleLength = parseFloat(setting.afterShiftRuleLength);
    return !isNaN(afterShiftRuleLength);
}

const isValidShiftStartTime = (shiftStartTime) => {
    return !isNaN(new Date(shiftStartTime).getTime());
}

const isWithinAllowedAfterShiftTime = (setting, shiftStartTime) => {
    const afterShiftRuleLength = parseFloat(setting.afterShiftRuleLength);
    const allowedAfterShiftTime = getCurrentUkgUserDateTime(
        new Date(shiftStartTime.getTime() + afterShiftRuleLength * 60 * 60 * 1000)
    ).toISOString();

    return getCurrentUkgUserDateTime(new Date()).toISOString() <= allowedAfterShiftTime;
}

export const isValidSettingAfterShiftStart = (setting, shiftStartTime) => {
    const isTimeAllowedAfterShiftValid = isSettingVisibleAndEnabled(setting) &&
        isValidAfterShiftRuleLength(setting) &&
        isValidShiftStartTime(shiftStartTime) &&
        isWithinAllowedAfterShiftTime(setting, shiftStartTime);
    return setting.isPartialShiftEnabled || isTimeAllowedAfterShiftValid;
};